import WebMercatorViewport from '@math.gl/web-mercator';
import { memo, useEffect, useRef } from 'react';
import { DEVICE_PIXEL_RATIO } from 'src/lib/player/constants';
import { t } from 'src/lib/player/i18n';
import type { TrackType } from 'src/lib/player/store';
import { noop } from 'src/lib/player/utils';
import { POI_HEIGHT, POI_WIDTH } from '../Map/MarkerContext';
import { MapFeatureListItem } from './MapFeatureListItem';

type Props = {
  track: TrackType;
  onClick: (track: TrackType, isSelectd: boolean) => void;
};

function drawTrack(
  track: TrackType,
  canvas: HTMLCanvasElement,
  scale: number = DEVICE_PIXEL_RATIO,
): void {
  const ctx = canvas.getContext('2d');
  if (ctx == null) return;

  ctx.setTransform(scale, 0, 0, scale, 0, 0);

  const mercatorProjection = new WebMercatorViewport({ width: 16500, height: 10000 });
  const points = track.coordinates.map((lnglatZ) => mercatorProjection.project(lnglatZ));

  if (points.length > 0) {
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (const [x, y] of points) {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }

    if (maxX > minX || maxY > minY) {
      const scale = maxX - minX > maxY - minY ? maxX - minX : maxY - minY;
      const scaleXY = (POI_WIDTH - 4) / scale;
      const offsetX = (POI_WIDTH - ((POI_WIDTH - 4) * (maxX - minX)) / scale) / 2;
      const offsetY = (POI_WIDTH - ((POI_WIDTH - 4) * (maxY - minY)) / scale) / 2;

      for (let i = 0; i < points.length; i++) {
        points[i][0] = (points[i][0] - minX) * scaleXY + offsetX;
        points[i][1] = (points[i][1] - minY) * scaleXY + offsetY;
      }

      ctx.globalAlpha = 1;
      ctx.strokeStyle = track.markerColor.hex;
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';

      ctx.moveTo(points[0][0], points[0][1]);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i][0], points[i][1]);
      }
      ctx.stroke();
    }
  }
}

const TrackIcon = memo(function TrackIcon({
  track,
  scale = 0.5,
}: {
  track: TrackType;
  scale?: number;
}): JSX.Element {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas == null) return;

    drawTrack(track, canvas, DEVICE_PIXEL_RATIO);
  }, [track]);

  return (
    <canvas
      ref={canvasRef}
      width={POI_WIDTH * DEVICE_PIXEL_RATIO}
      height={POI_HEIGHT * DEVICE_PIXEL_RATIO}
      style={{ width: POI_WIDTH * scale, height: POI_HEIGHT * scale }}
    />
  );
});

export default function TrackListItem({ track, onClick = noop }: Props): JSX.Element {
  return (
    <MapFeatureListItem
      id={track.id}
      kind="track"
      label={track.name || t('track')}
      icon={<TrackIcon track={track} />}
      onClick={(isSelected) => onClick(track, isSelected)}
    />
  );
}
